/**
 * V70-L — 게임대회 다크 테마 토큰 (대시보드 시범 적용 전용)
 *
 * V70-L 2차 — 사용자 선택: 차분한 단색 그레이 페이지 배경 + 블랙 계열 섹션 박스·목록 행 (명도 차이로 구분).
 * V70-L 3차 — 최종 방향: 블랙에 가까운 어두운 차콜 페이지 + 그보다 더 어두운 블랙 섹션·깊은 블랙 공지 행.
 *   중요공지(isPinned)만 노란 네온 테두리. 주요 액션 블루·브랜드 골드는 1차 값 유지.
 * 상태 색(진행·종료·대기·오류)은 기존 의미(초록=완료, 빨강=LIVE/오류, 파랑=예정, 주황=중요)를 유지하고
 * 다크 배경에서 읽히도록 반투명 배경 + 밝은 글자로만 바꾼다. 과도한 그림자·네온·애니메이션 없음.
 * V70-M — 사용자 승인(V70-L 3차) 후 게임대회 6개 페이지 전체에 확대 적용.
 *   관리자 화면·공용 편집기·일반 ERP 는 이 토큰을 쓰지 않는다.
 */
import type React from 'react';

export const DARK = {
  /** 페이지 바깥 배경·헤더 — 어두운 차콜 그레이 (단색, 그라데이션·패턴 없음) */
  page: '#22262E',
  /** 콘텐츠 섹션 박스·대진표 카드·모달 — 페이지보다 더 어두운 블랙 */
  card: '#111318',
  /** 공지·중계·결과 목록 개별 행 — 깊은 블랙 */
  row: '#0B0D12',
  /** 보조 영역 (보조 버튼·이미지 영역·빈 목록 박스) */
  surface: '#191C24',
  border: '#303540',
  text: '#F5F6FA',
  textMuted: '#AEB8C8',
  /** 페이지 배경 위에 바로 놓이는 보조 글자 — 3차 차콜 배경에서는 가이드 보조 글자 그대로 (7.58:1) */
  textMutedOnPage: '#AEB8C8',
  /** V70-M — 주요 버튼: 흰 글자 대비 5.0:1 확보 (기존 #168CFA 는 3.41:1) */
  primary: '#0B6FD0',
  gold: '#E8C47C',
  link: '#6CB8FF',
} as const;

/** 상태 배지 — 기존 의미 유지 (배경 반투명 + 밝은 글자) */
export const DARK_STATUS = {
  done: { background: 'rgba(34, 197, 94, 0.16)', color: '#86EFAC', border: '1px solid rgba(34, 197, 94, 0.35)' },
  live: { background: '#DC2626', color: '#FFFFFF', border: '1px solid #DC2626' },
  scheduled: { background: 'rgba(22, 140, 250, 0.16)', color: '#8CC8FF', border: '1px solid rgba(22, 140, 250, 0.4)' },
  pinned: { background: '#F59E0B', color: '#1A1205', border: '1px solid #F59E0B' },
} as const;

export const darkCard: React.CSSProperties = {
  background: DARK.card,
  border: `1px solid ${DARK.border}`,
  borderRadius: 12,
};

export const darkSectionTitle: React.CSSProperties = {
  margin: 0, fontSize: 16, fontWeight: 700, color: DARK.text,
};

/** 카드 안 목록 행 (공지·중계·결과) */
export const darkRow: React.CSSProperties = {
  background: DARK.row,
  border: `1px solid ${DARK.border}`,
  borderRadius: 8,
};

/**
 * V70-L 2차 — 중요공지(isPinned=true) 행 전용: 노란 네온 테두리 + 은은한 바깥 glow.
 *   내부 배경·둥글기·테두리 두께(1px)는 일반 행과 같아 행 높이 변화 없음. 점멸·움직임 없음.
 */
export const NEON_YELLOW = '#FFD400';
export const darkPinnedRow: React.CSSProperties = {
  ...darkRow,
  border: `1px solid ${NEON_YELLOW}`,
  boxShadow: '0 0 6px rgba(255, 212, 0, 0.45), 0 0 14px rgba(255, 212, 0, 0.15)',
};

/** 강조 행 (중요 공지·우승 표시) — 골드는 테두리·옅은 배경에만 */
export const darkHighlight: React.CSSProperties = {
  background: 'rgba(232, 196, 124, 0.08)',
  border: '1px solid rgba(232, 196, 124, 0.38)',
  borderRadius: 8,
};

export const darkPrimaryBtn: React.CSSProperties = {
  padding: '5px 12px', background: DARK.primary, color: '#FFFFFF', border: `1px solid ${DARK.primary}`,
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700,
};

export const darkSecondaryBtn: React.CSSProperties = {
  padding: '5px 12px', background: DARK.surface, color: DARK.text, border: `1px solid ${DARK.border}`,
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

export const darkDangerBtn: React.CSSProperties = {
  padding: '5px 12px', background: 'rgba(239, 68, 68, 0.14)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.45)',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
};

// V70-L 2차 — 그레이 배경 위에서도 읽히도록 불투명한 어두운 빨강 바탕 (오류 의미 색 유지)
export const darkErrorBox: React.CSSProperties = {
  padding: 10, color: '#FCA5A5', background: '#2A1418',
  border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: 8, fontSize: 13, marginBottom: 12,
};

export const darkEmptyBox: React.CSSProperties = {
  padding: 24, textAlign: 'center', color: DARK.textMuted, background: DARK.card,
  border: `1px dashed ${DARK.border}`, borderRadius: 12, fontSize: 13,
};

export const darkModalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.65)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};

export const darkModalBox: React.CSSProperties = {
  background: DARK.card, color: DARK.text, border: `1px solid ${DARK.border}`, borderRadius: 12,
  width: '100%', overflow: 'auto', padding: 20,
};

// ─── V70-M — 6개 페이지 공통 요소 ───

/** 상태 의미 색(관리자 화면과 공유하는 원래 색)을 다크 배경용 배지로: 어두운 틴트 바탕 + 밝은 틴트 글자 (대비 확보, 색 의미 유지) */
export function darkTintBadge(hex: string): React.CSSProperties {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return { background: DARK.surface, color: DARK.text, border: `1px solid ${DARK.border}` };
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x, 16));
  const card = [0x11, 0x13, 0x18];
  const bg = [r, g, b].map((c, i) => Math.round(c * 0.28 + card[i] * 0.72));
  const fg = [r, g, b].map((c) => Math.round(c + (255 - c) * 0.6));
  return {
    background: `rgb(${bg.join(', ')})`,
    color: `rgb(${fg.join(', ')})`,
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.55)`,
  };
}

/** 중립 배지 (모드·승인 방식 등 의미 색이 없는 표시) */
export const darkNeutralBadge: React.CSSProperties = {
  background: DARK.surface, color: DARK.textMuted, border: `1px solid ${DARK.border}`,
};

/** 입력창·선택상자·여러 줄 입력 — 브라우저 기본 컨트롤도 어둡게 (colorScheme) */
export const darkInput: React.CSSProperties = {
  background: DARK.row, color: DARK.text, border: `1px solid ${DARK.border}`, colorScheme: 'dark',
};

/** 주의 안내 (마감·인원 부족 등) — 주황 의미 유지 */
export const darkWarnBox: React.CSSProperties = {
  background: '#2A2210', border: '1px solid rgba(245, 158, 11, 0.45)', color: '#FCD34D',
};

/** 보조 정보 박스 (현재 내 상태 등) */
export const darkInfoBox: React.CSSProperties = {
  background: DARK.surface, border: `1px solid ${DARK.border}`, color: DARK.text,
};

/** 강조 수치 (참가 인원 등) — 블루 계열 밝은 글자 */
export const DARK_ACCENT_TEXT = '#8CC8FF';

/** 표 (리더보드) */
export const darkTh: React.CSSProperties = { color: DARK.textMuted, borderBottom: `1px solid ${DARK.border}` };
export const darkTd: React.CSSProperties = { color: DARK.text, borderBottom: `1px solid ${DARK.border}` };
/** 빈 순위 칸 "—" — 흐리지만 읽히는 회색 (#AEB8C8 의 70%) */
export const DARK_EMPTY_CELL = '#7D8696';

/**
 * 공지·결과 본문(RichContentViewer)을 다크 모달에서 읽히게 하는 범위 한정 스타일.
 *   공용 컴포넌트는 수정하지 않고 className(v70l-dark-rich) 범위에서만 덮어쓴다.
 *   편집기 팔레트의 어두운 글자색(#000000·#374151·#6b7280)은 다크 배경에서 보이지 않으므로 밝은 색으로 바꾼다.
 *   나머지 팔레트(빨강·주황·초록·파랑·보라)는 작성 의도대로 유지.
 */
export const DARK_RICH_CLASS = 'v70l-dark-rich';
//   공용 뷰어 자체 규칙(.rich-content-viewer a 등)보다 우선하도록 두 클래스를 함께 지정 (뷰어 <style> 이 뒤에 와도 적용)
const R = `.rich-content-viewer.${DARK_RICH_CLASS}`;
export const DARK_RICH_CSS = `
${R} a { color: ${DARK.link}; }
${R} blockquote { border-left-color: ${DARK.border}; color: ${DARK.textMuted}; }
${R} hr { border-top-color: ${DARK.border}; }
${R} th, ${R} td { border-color: ${DARK.border}; }
${R} th { background: ${DARK.surface}; color: ${DARK.text}; }
${R} [style*="color: #000000"], ${R} [style*="color: rgb(0, 0, 0)"],
${R} [style*="color: #374151"], ${R} [style*="color: rgb(55, 65, 81)"] { color: ${DARK.text} !important; }
${R} [style*="color: #6b7280"], ${R} [style*="color: rgb(107, 114, 128)"] { color: ${DARK.textMuted} !important; }
`;
